import { ThemeOptions } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';
import React, { createContext, useContext, useState } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { ThemeContextType } from '@/shared/features/theme-switcher/context/theme-context.types';
import { breakpointsBaseTheme } from '@/shared/features/theme-switcher/themes/base/breakpoints-base-theme';
import { typographyBaseTheme } from '@/shared/features/theme-switcher/themes/base/typography-base-theme';
import { lightTheme } from '@/shared/features/theme-switcher/themes/colors/light-theme';
import { midnightBlueTheme } from '@/shared/features/theme-switcher/themes/colors/midnight-blue-theme';
import { ThemeEnum } from '@/shared/types/common.types';
import { getReseller } from '@/shared/utils/reseller/reseller';
import { THEME_STORAGE_KEY } from '@/shared/utils/storage/storage-keys';

export const useAppTheme = () => useContext(ThemeContext);

export const ThemeContext = createContext<ThemeContextType>({
  selectedTheme: ThemeEnum.DARK,
  setSelectedTheme: () => {},
});

// Eagerly import all reseller dark themes
const resellerThemes = import.meta.glob<{ darkTheme: ThemeOptions }>(
  '../../../../reseller/*/theme/dark-theme.ts',
  { eager: true },
);

// Get the reseller dark theme based on environment variable
const getResellerDarkTheme = (): ThemeOptions => {
  const reseller = getReseller();

  if (!reseller) {
    throw new Error('VITE_APP_SUPER_TENANT is not defined');
  }

  // Find the theme that matches the reseller name
  const themeEntry = Object.entries(resellerThemes).find(([path]) =>
    path.includes(`/${reseller}/theme/dark-theme.ts`),
  );

  if (!themeEntry) {
    console.error(`Dark theme not found for reseller: ${reseller}`);
    throw new Error(`Dark theme not found for reseller: ${reseller}`);
  }

  return themeEntry[1].darkTheme;
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedTheme, setSelectedTheme] = useState<ThemeEnum>(
    (localStorage.getItem(THEME_STORAGE_KEY) ?? ThemeEnum.DARK) as ThemeEnum,
  );

  const _createTheme = (theme: ThemeOptions) => {
    return createTheme({
      ...breakpointsBaseTheme,
      ...typographyBaseTheme,
      ...theme,
    });
  };

  const getTheme = () => {
    if (selectedTheme === ThemeEnum.MIDNIGHT_BLUE) {
      return _createTheme(midnightBlueTheme);
    }

    if (selectedTheme === ThemeEnum.DARK) {
      return _createTheme(getResellerDarkTheme());
    }

    return _createTheme(lightTheme);
  };

  const _setSelectedTheme = (theme: ThemeEnum) => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    setSelectedTheme(theme);
  };

  return (
    <ThemeContext.Provider
      value={{
        selectedTheme: selectedTheme,
        setSelectedTheme: _setSelectedTheme,
      }}
    >
      <MUIThemeProvider theme={getTheme()}>
        <StyledComponentsThemeProvider theme={getTheme()}>
          {children}
        </StyledComponentsThemeProvider>
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
